import { Box, Flex, Select, Tooltip, useColorModeValue } from '@chakra-ui/react';
import { startOfWeek, endOfWeek } from 'date-fns';
import { useMemo, useState } from 'react';
import { translate } from '../../../i18n';
import { CourtAvailabilityDto } from '../../../models/court/dto/court-availability.dto';
import { SportType } from '../../../models/court/dto/court.dto';
import { WeekPicker } from '../../shared/components/WeekPicker';
// The `capacity` a pricing row carries when it prices EVERY capacity. It is
// the column default, so every row written before multi-capacity pricing has
// it — rendering it as "-1 players", which this grid did, is nonsense.
import { WILDCARD_CAPACITY } from '../../../utils/sport-capacity';

const DAYS_OF_WEEK = [1, 2, 3, 4, 5, 6, 0];
const HOURS_OF_DAY = Array.from({ length: 24 }, (_, i) => i);

type CellPricing = {
    price: number;
    currency: string;
    priceDuration: number;
    capacity: number;
    /**
     * Carried so the capacity selection can group a CELL's rows the way the
     * server groups them — by `(sport, slotDurationInMs)`. Without it the
     * exact-beats-wildcard rule would suppress one sport's wildcard row on the
     * strength of another sport's exact row.
     */
    sport?: SportType;
};

type CourtPricingHeatmapProps = {
    rules: CourtAvailabilityDto[];
    /**
     * Called with the Monday 00:00 and Sunday 23:59 of the week the operator
     * navigated to. The grid only ever FILTERS the rows it is given, so an
     * owner that fetched a fixed window (the court page fetches today ± 7
     * days) rendered every week outside it as "priced nowhere" — a false
     * claim about money. Owners use this to refetch for the week shown.
     */
    onWeekChange?: (weekStart: Date, weekEnd: Date) => void;
};

/**
 * What one cell shows. `null` when nothing matches the filters.
 *
 * `price` is the LOWEST matching price and `hasMultiplePrices` says whether the
 * others disagree — the grid is one number per hour, so with several capacities
 * priced differently the only honest single number is a "from" price plus a
 * marker that there is more behind it. The tooltip carries the full list.
 */
/**
 * The subset of an availability this grid reads. Typed structurally so the
 * cell builder can be exercised without constructing a whole DTO.
 */
type PricedWindow = {
    startTime: string | Date;
    endTime: string | Date;
    pricings: { price: number; currency: string; slotDurationInMs: number; sport?: SportType; capacity?: number }[];
};

export type CellFilters = {
    sport: SportType | 'all';
    duration: number | 'all';
    capacity: number | 'all';
};

/** A week grid is 7 x 24 cells; no window can paint one it has not already painted. */
const MAX_HOURS_PAINTED = 7 * 24;

/** The pricing rows this grid reads, as one availability window carries them. */
type HeatmapPricing = PricedWindow['pricings'][number];

/**
 * The rows that actually price a booking at `capacity` — D1's selection rule,
 * not an equality test.
 *
 * The server prefers the row whose `capacity` matches the request and **falls
 * back to the wildcard row** when there is none (`court-booking-base-fees.ts`;
 * pinned as D1). Filtering the grid by `capacity === 2` therefore used to blank
 * every hour on a court priced with a single capacity-agnostic row — which is
 * every court at every organization that never opted into multi-capacity
 * pricing, and every legacy row at the ones that did. The operator was looking
 * at "2 players: no price" for a slot that sells at 2 players all day, and the
 * screen that says so is the one they open to decide whether to add a row.
 *
 * Selection is per `(sport, slotDurationInMs)` because that is the key the
 * server matches on: a padel hour priced €30 at capacity 2 with a €44 wildcard
 * shows €30 at 2 players (the exact row wins) and €44 at 4 (nothing exact, so
 * the wildcard prices it), which is exactly what each booking would be charged.
 * Rows selected through the fallback keep their wildcard capacity, so the
 * tooltip still names them "all capacities" rather than claiming a row that
 * does not exist.
 */
export function selectPricingsForCapacity(
    pricings: HeatmapPricing[],
    capacity: number | 'all',
): HeatmapPricing[] {
    return selectRowsForCapacity(
        pricings,
        capacity,
        pricing => `${String(pricing.sport)}|${pricing.slotDurationInMs}`,
        pricing => pricing.capacity,
    );
}

/**
 * D1's exact-beats-wildcard rule over any row shape, grouped by whatever key
 * the caller says the server matches on.
 *
 * Extracted because the rule has to run over a CELL's merged rows, not over one
 * availability's — see `buildPricingCellMap`. The two callers must not drift:
 * a cell and the tooltip inside it are the same claim.
 */
function selectRowsForCapacity<T>(
    rows: T[],
    capacity: number | 'all',
    groupKey: (row: T) => string,
    capacityOfRow: (row: T) => number | undefined,
): T[] {
    if (capacity === 'all') {
        return rows;
    }

    const groups = new Map<string, T[]>();
    rows.forEach(row => {
        const key = groupKey(row);
        groups.set(key, [...(groups.get(key) ?? []), row]);
    });

    const selected: T[] = [];
    groups.forEach(group => {
        const exact = group.filter(row => (capacityOfRow(row) ?? WILDCARD_CAPACITY) === capacity);
        if (exact.length > 0) {
            selected.push(...exact);
            return;
        }
        selected.push(
            ...group.filter(row => (capacityOfRow(row) ?? WILDCARD_CAPACITY) === WILDCARD_CAPACITY),
        );
    });

    return selected;
}

/**
 * The same selection, applied to one CELL's merged rows.
 *
 * This is the step that has to exist at cell level rather than at availability
 * level. With `returnAsOneChunk === false` — the value both dashboard forms
 * send and the DB column default — `expandCompactAvailabilities` emits one
 * `CourtAvailability` PER CONFIG, so a padel hour priced €30 at capacity 2 with
 * a €22 wildcard arrives as TWO windows carrying one row each. Selecting inside
 * each window then never has both rows in hand: the exact row cannot suppress a
 * wildcard row that lives on a different window, both reach the cell, and the
 * cell headlines `Math.min` — €22, for a slot the server charges €30 at that
 * capacity (`court-booking-base-fees.ts` pools candidates across every covering
 * availability BEFORE applying the rule). The operator quoting off the grid
 * understated by €8.
 */
export function selectCellEntriesForCapacity(
    entries: CellPricing[],
    capacity: number | 'all',
): CellPricing[] {
    return selectRowsForCapacity(
        entries,
        capacity,
        entry => `${String(entry.sport)}|${entry.priceDuration}`,
        entry => entry.capacity,
    );
}

// `sport` is part of the identity because the capacity selection groups on it:
// collapsing a padel row into an identically-priced tennis row would hand the
// selection one group where the server has two. The tooltip de-duplicates the
// rendered LINES instead, so a collapse the operator would have seen as one
// line still reads as one line.
const cellEntryKey = (entry: CellPricing) =>
    `${String(entry.sport)}|${entry.capacity}|${entry.priceDuration}|${entry.price}|${entry.currency}`;

/**
 * Add `incoming` to the entries a cell already holds, in a stable order.
 *
 * Two availabilities legitimately cover the same hour — with
 * `returnAsOneChunk === false` (the DB default) `expandCompactAvailabilities`
 * emits one `CourtAvailability` PER CONFIG, so a padel hour priced €30 at
 * capacity 2 and €44 at capacity 4 arrives as two rows over the same window.
 * The cell used to `set` the second over the first, so it showed €30 or €44
 * with no marker and which one won was the order the DB happened to return.
 *
 * Rows identical in capacity, duration, price and currency are collapsed:
 * seeing "4 players: €44" twice in a tooltip states nothing the operator can
 * act on. The sort makes the tooltip order independent of the payload order,
 * so the same week renders the same way on every load.
 */
export function mergeCellEntries(existing: CellPricing[], incoming: CellPricing[]): CellPricing[] {
    const seen = new Set(existing.map(cellEntryKey));
    const merged = [...existing];

    incoming.forEach(entry => {
        const key = cellEntryKey(entry);
        if (seen.has(key)) return;
        seen.add(key);
        merged.push(entry);
    });

    return merged.sort(
        (a, b) =>
            a.capacity - b.capacity ||
            a.priceDuration - b.priceDuration ||
            a.price - b.price ||
            a.currency.localeCompare(b.currency),
    );
}

/**
 * day-of-week -> hour -> every price that covers that hour and matches the filters.
 *
 * `endTime` is the EXCLUSIVE end instant of the window, so the painted hours
 * are those the window actually covers: 08:00–10:00 is hours 8 and 9, not
 * 8, 9 and 10. The previous version walked a day/hour counter with
 * `while (currentHour <= endHour)`, which painted a price into the hour the
 * court closes — and, because every per-hour availability of a sliced
 * schedule ends where the next begins, wrote every interior hour twice.
 */
export function buildPricingCellMap(rules: PricedWindow[], filters: CellFilters) {
    const map = new Map<number, Map<number, CellPricing[]>>();

    // Initialize the map with empty values — an hour with no matching
    // pricing must render as "no price", not as a €0 court.
    DAYS_OF_WEEK.forEach(day => {
        const hourMap = new Map<number, CellPricing[]>();
        HOURS_OF_DAY.forEach(hour => {
            hourMap.set(hour, []);
        });
        map.set(day, hourMap);
    });

    rules.forEach(rule => {
        const startTime = new Date(rule.startTime);
        const endTime = new Date(rule.endTime);
        // A zero-length or inverted window covers no hour. Guarding here also
        // keeps an unparseable date (NaN) from spinning the loop below.
        if (!(startTime.getTime() < endTime.getTime())) return;

        // Every pricing that matches the selected filters. No
        // `|| rule.pricings[0]` fallback: that painted a completely
        // unrelated sport's or duration's price into a cell the operator
        // had explicitly filtered it out of.
        //
        // Sport and duration are equality and are applied HERE; capacity is
        // not, and is applied once per CELL after this loop — see
        // `selectCellEntriesForCapacity` for why selecting per availability
        // silently showed the wildcard price next to the exact one.
        const matching = rule.pricings.filter(p => {
            const sportMatch = filters.sport === 'all' || p.sport === filters.sport;
            const durationMatch = filters.duration === 'all' || p.slotDurationInMs === filters.duration;
            return sportMatch && durationMatch;
        });
        if (matching.length === 0) return;

        const entries: CellPricing[] = matching.map(pricing => ({
            price: pricing.price,
            currency: pricing.currency,
            priceDuration: pricing.slotDurationInMs,
            capacity: pricing.capacity ?? WILDCARD_CAPACITY,
            sport: pricing.sport,
        }));

        // Walk the window itself rather than reconstructing it from
        // day-of-week arithmetic, so a block crossing midnight (or ending
        // exactly at it) needs no special case.
        const cursor = new Date(startTime);
        cursor.setMinutes(0, 0, 0);
        for (let painted = 0; cursor.getTime() < endTime.getTime() && painted < MAX_HOURS_PAINTED; painted++) {
            const hourMap = map.get(cursor.getDay());
            if (hourMap) {
                const hour = cursor.getHours();
                hourMap.set(hour, mergeCellEntries(hourMap.get(hour) ?? [], entries));
            }
            cursor.setHours(cursor.getHours() + 1);
        }
    });

    // The capacity rule runs LAST, once per cell, with every covering
    // availability's rows in hand — which is the only place it can reproduce
    // what the server does. Applied here rather than at the render site so the
    // colour scale, the headline number and the tooltip all read one set.
    map.forEach(hourMap => {
        hourMap.forEach((entries, hour) => {
            hourMap.set(hour, selectCellEntriesForCapacity(entries, filters.capacity));
        });
    });

    return map;
}

export function summarizeCellPricing(entries: CellPricing[]) {
    if (entries.length === 0) return null;
    const prices = entries.map(entry => entry.price);
    const lowest = Math.min(...prices);
    const cheapest = entries.find(entry => entry.price === lowest)!;
    return {
        price: lowest,
        currency: cheapest.currency,
        hasMultiplePrices: new Set(prices).size > 1,
        entries,
    };
}

export default function CourtPricingHeatmap({ rules, onWeekChange }: CourtPricingHeatmapProps) {
    // State for selected week, sport, duration and capacity
    const [selectedWeek, setSelectedWeekState] = useState<Date>(new Date());
    const setSelectedWeek = (week: Date) => {
        setSelectedWeekState(week);
        onWeekChange?.(
            startOfWeek(week, { weekStartsOn: 1 }),
            endOfWeek(week, { weekStartsOn: 1 }),
        );
    };
    const [selectedSport, setSelectedSport] = useState<SportType | 'all'>('all');
    const [selectedDuration, setSelectedDuration] = useState<number | 'all'>('all');
    const [selectedCapacity, setSelectedCapacity] = useState<number | 'all'>('all');

    // Get unique sports from all rules
    const availableSports = useMemo(() => {
        const sportsSet = new Set<SportType>();
        rules.forEach(rule => {
            rule.pricings.forEach(pricing => {
                if (pricing.sport) {
                    sportsSet.add(pricing.sport);
                }
            });
        });
        return Array.from(sportsSet);
    }, [rules]);

    // Get unique durations from all rules
    const availableDurations = useMemo(() => {
        const durationsSet = new Set<number>();
        rules.forEach(rule => {
            rule.pricings.forEach(pricing => {
                durationsSet.add(pricing.slotDurationInMs);
            });
        });
        return Array.from(durationsSet).sort((a, b) => a - b);
    }, [rules]);

    // Get unique capacities from all rules.
    //
    // The WILDCARD sentinel is excluded deliberately. `toDto()` always emits
    // `capacity`, and it is `-1` on every legacy row, so the sentinel used to
    // enter this set and — since `formatCapacity(-1)` renders the same string
    // as the static option above it — the dropdown offered "All Capacities"
    // TWICE, with different behaviour: the static one shows every row, the
    // sentinel one routes through the selection and shows only the wildcard
    // rows. An operator picking the second read "All Capacities: €44" for a
    // court that charges €30 at two players. The static option already covers
    // "show me everything"; a filter for "rows that price every capacity" is
    // not a capacity the venue sells.
    const availableCapacities = useMemo(() => {
        const capacitiesSet = new Set<number>();
        rules.forEach(rule => {
            rule.pricings.forEach(pricing => {
                if (pricing.capacity !== undefined && pricing.capacity !== WILDCARD_CAPACITY) {
                    capacitiesSet.add(pricing.capacity);
                }
            });
        });
        return Array.from(capacitiesSet).sort((a, b) => a - b);
    }, [rules]);

    // Filter rules based on selected week, sport, duration and capacity
    const filteredRules = useMemo(() => {
        const weekStartDate = startOfWeek(selectedWeek, { weekStartsOn: 1 });
        const weekEndDate = endOfWeek(selectedWeek, { weekStartsOn: 1 });

        return rules.filter(rule => {
            const ruleDate = new Date(rule.startTime);
            const isInSelectedWeek = ruleDate >= weekStartDate && ruleDate <= weekEndDate;

            const matchesSport = selectedSport === 'all' ||
                rule.pricings.some(pricing => pricing.sport === selectedSport);

            const matchesDuration = selectedDuration === 'all' ||
                rule.pricings.some(pricing => pricing.slotDurationInMs === selectedDuration);

            // A wildcard row prices the selected capacity too (D1's fallback),
            // so a window carrying only capacity-agnostic rows must survive
            // this filter — dropping it here blanked the whole week before the
            // cell builder ever got the chance to price it.
            const matchesCapacity = selectedCapacity === 'all' ||
                rule.pricings.some(pricing => {
                    const capacity = pricing.capacity ?? WILDCARD_CAPACITY;
                    return capacity === selectedCapacity || capacity === WILDCARD_CAPACITY;
                });

            return isInSelectedWeek && matchesSport && matchesDuration && matchesCapacity;
        });
    }, [rules, selectedWeek, selectedSport, selectedDuration, selectedCapacity]);

    // A cell holds EVERY pricing row that matches the current filters, not one
    // of them. With multi-capacity pricing a single hour legitimately carries
    // several prices (2 players: €30 / 4 players: €44); the previous version
    // took `rule.pricings.find(...)` and rendered that one number as "the"
    // price, so an operator comparing capacities saw whichever row happened to
    // come first. Chosen representation: the cell shows the LOWEST matching
    // price with a `+` when the matches disagree, and the tooltip enumerates
    // every capacity with its own price.
    const priceMap = useMemo(
        () =>
            buildPricingCellMap(filteredRules, {
                sport: selectedSport,
                duration: selectedDuration,
                capacity: selectedCapacity,
            }),
        [filteredRules, selectedSport, selectedDuration, selectedCapacity],
    );

    // Calculate the maximum price for color scaling
    const maxPrice = useMemo(() => {
        let max = 0;
        priceMap.forEach(hourMap => {
            hourMap.forEach(entries => {
                entries.forEach(entry => {
                    if (entry.price > max) max = entry.price;
                });
            });
        });
        return max;
    }, [priceMap]);

    // Helper function to get color intensity based on price
    const getColorIntensity = (price: number) => {
        if (maxPrice === 0) return 'rgb(241, 241, 241)';
        const intensity = (price / maxPrice) * 0.8; // Max 80% intensity to keep text readable
        return `rgba(66, 153, 225, ${intensity})`; // Chakra blue
    };

    // Helper function to format price
    const formatPrice = (price: number, currency: string) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency.toUpperCase(),
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
        }).format(price);
    };

    // Helper function to format duration
    const formatDuration = (durationInMs: number) => {
        return Math.round(durationInMs / 60000);
    };

    // Helper function to format capacity
    const formatCapacity = (capacity: number) => {
        // `-1` is the wildcard: this row prices every capacity. It used to
        // render as the literal "-1 players".
        return capacity === WILDCARD_CAPACITY
            ? translate('courts.all_capacities')
            : `${capacity} ${translate('game_capacity_options.players')}`;
    };


    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const headerBg = useColorModeValue('gray.50', 'gray.700');
    const stickyHeaderBg = useColorModeValue('white', 'gray.800');

    return (
            <Box p={4} w="100%">
                <Flex justify="space-between" align="center" mb={4}>
                    <Flex gap={4} alignItems="center" justifyContent="flex-end" w={'100%'} wrap="wrap">
                        <WeekPicker
                            selectedDate={selectedWeek}
                            onChange={setSelectedWeek}
                            size="sm"
                        />
                        <Select
                            value={selectedSport}
                            onChange={(e) => setSelectedSport(e.target.value as SportType | 'all')}
                            width="200px"
                            size="sm"
                        >
                            <option value="all">{translate('courts.all_sports')}</option>
                            {availableSports.map(sport => (
                                <option key={sport} value={sport}>
                                    {sport}
                                </option>
                            ))}
                        </Select>
                        <Select
                            value={selectedDuration}
                            onChange={(e) => setSelectedDuration(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                            width="200px"
                            size="sm"
                        >
                            <option value="all">{translate('courts.all_durations')}</option>
                            {availableDurations.map(duration => (
                                <option key={duration} value={duration}>
                                    {formatDuration(duration)} min
                                </option>
                            ))}
                        </Select>
                        <Select
                            value={selectedCapacity}
                            onChange={(e) => setSelectedCapacity(e.target.value === 'all' ? 'all' : Number(e.target.value))}
                            width="200px"
                            size="sm"
                        >
                            <option value="all">{translate('courts.all_capacities')}</option>
                            {availableCapacities.map(capacity => (
                                <option key={capacity} value={capacity}>
                                    {formatCapacity(capacity)}
                                </option>
                            ))}
                        </Select>
                    </Flex>
                </Flex>

                <Box
                    maxH="500px"
                    overflowY="auto"
                    overflowX="auto"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor={borderColor}
                    css={{
                        '&::-webkit-scrollbar': {
                            width: '8px',
                            height: '8px',
                        },
                        '&::-webkit-scrollbar-track': {
                            background: '#f1f1f1',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: '#888',
                            borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: '#555',
                        },
                    }}
                >
                    <Box minW="fit-content">
                        {/* Header row */}
                        <Box
                            display="flex"
                            position="sticky"
                            top={0}
                            bg={stickyHeaderBg}
                            zIndex={1}
                            borderBottomWidth="1px"
                            borderColor={borderColor}
                        >
                            {/* Empty corner cell */}
                            <Box
                                minW="60px"
                                h="40px"
                                borderRightWidth="1px"
                                borderColor={borderColor}
                                bg={headerBg}
                                position="sticky"
                                left={0}
                                zIndex={2}
                            />

                            {/* Hour headers */}
                            {HOURS_OF_DAY.map(hour => (
                                <Box
                                    key={hour}
                                    minW="60px"
                                    h="40px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    fontWeight="bold"
                                    fontSize="xs"
                                    bg={headerBg}
                                    borderRightWidth="1px"
                                    borderColor={borderColor}
                                >
                                    {`${hour}:00`}
                                </Box>
                            ))}
                        </Box>

                        {/* Grid rows */}
                        {DAYS_OF_WEEK.map(day => (
                            <Box key={day} display="flex">
                                {/* Day header */}
                                <Box
                                    minW="60px"
                                    h="40px"
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    fontWeight="bold"
                                    fontSize="xs"
                                    bg={headerBg}
                                    borderRightWidth="1px"
                                    borderBottomWidth="1px"
                                    borderColor={borderColor}
                                    position="sticky"
                                    left={0}
                                    zIndex={1}
                                >
                                    {translate(`days.${day}` as any)}
                                </Box>

                                {/* Hour cells */}
                                {HOURS_OF_DAY.map(hour => {
                                    const cell = summarizeCellPricing(priceMap.get(day)?.get(hour) ?? []);
                                    // One line per capacity, so a cell showing
                                    // "30 €+" tells the operator exactly which
                                    // capacities cost what.
                                    // De-duplicated on the rendered LINE: two
                                    // sports priced identically at the same
                                    // capacity and duration are two entries
                                    // (the capacity selection needs them
                                    // apart) but one sentence, and printing
                                    // that sentence twice states nothing.
                                    const tooltipLabel = cell
                                        ? Array.from(
                                            new Set(
                                                cell.entries
                                                    .slice()
                                                    .sort((a, b) => a.capacity - b.capacity)
                                                    .map(entry =>
                                                        `${formatCapacity(entry.capacity)}: ${formatPrice(entry.price, entry.currency)} / ${formatDuration(entry.priceDuration)} min`,
                                                    ),
                                            ),
                                        ).join('\n')
                                        : translate('court.no_price');

                                    return (
                                        <Tooltip
                                            key={hour}
                                            label={tooltipLabel}
                                            hasArrow
                                            placement="top"
                                            whiteSpace="pre-line"
                                        >
                                            <Box
                                                minW="60px"
                                                h="40px"
                                                bg={getColorIntensity(cell?.price || 0)}
                                                borderRightWidth="1px"
                                                borderBottomWidth="1px"
                                                borderColor={borderColor}
                                                display="flex"
                                                alignItems="center"
                                                justifyContent="center"
                                                fontSize="xs"
                                                fontWeight="semibold"
                                            >
                                                {cell
                                                    ? `${formatPrice(cell.price, cell.currency)}${cell.hasMultiplePrices ? '+' : ''}`
                                                    : '-'
                                                }
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>
    );
}